import { gql, useLazyQuery } from '@apollo/client';
import Select from '@orn/shared-web/components/Select';
import * as React from 'react';

import type {
  AdminListMerchantsQuery,
  AdminListMerchantsQueryVariables,
} from './__generated__/AdminSelectMerchant.gql';

const ADMIN_LIST_MERCHANTS = gql`
  query AdminListMerchants($familyId: ID!) {
    adminListMerchants(familyId: $familyId) {
      id
      name
    }
  }
`;

interface IAdminSelectMerchantProps {
  selectedFamilyId: string | null;
  selectedMerchantId: string | null;
  onSelectionChange: (merchantId: string | null, merchantName?: string | null) => void;
  label?: string;
  className?: string;
}

/**
 * Admin merchant picker. Loads the list of merchants for the selected family
 * via the mops-api `adminListMerchants` query and renders them in a `Select`.
 * Emits both the merchant id and (best-effort) display name so callers can
 * echo the choice in their own UI without re-querying.
 */
const AdminSelectMerchant = ({
  selectedFamilyId,
  selectedMerchantId,
  onSelectionChange,
  label = 'Merchant',
  className,
}: IAdminSelectMerchantProps) => {
  const [loadMerchants, { data: merchantsData, loading: merchantsLoading }] = useLazyQuery<
    AdminListMerchantsQuery,
    AdminListMerchantsQueryVariables
  >(ADMIN_LIST_MERCHANTS);
  const previousSelectedFamilyIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!selectedFamilyId) {
      previousSelectedFamilyIdRef.current = null;
      onSelectionChange(null, null);
      return;
    }

    if (previousSelectedFamilyIdRef.current != null && previousSelectedFamilyIdRef.current !== selectedFamilyId) {
      onSelectionChange(null, null);
    }

    previousSelectedFamilyIdRef.current = selectedFamilyId;
    void loadMerchants({ variables: { familyId: selectedFamilyId } });
  }, [selectedFamilyId, loadMerchants, onSelectionChange]);

  const merchants = React.useMemo(() => {
    if (!selectedFamilyId) {
      return [];
    }

    const merchantItems =
      merchantsData?.adminListMerchants.map((merchant) => ({
        id: merchant.id,
        label: merchant.name || `Merchant ${merchant.id}`,
      })) ?? [];

    if (merchantItems.length === 0) {
      return [{ id: 'no-merchants-found', label: 'No items found', isDisabled: true }];
    }

    return merchantItems;
  }, [selectedFamilyId, merchantsData?.adminListMerchants]);

  return (
    <Select
      label={label}
      placeholder={selectedFamilyId ? 'Select a merchant...' : 'Select a user first'}
      value={selectedMerchantId || null}
      onChange={(key) => {
        const nextMerchantId = key ? (key as string) : null;
        if (!nextMerchantId || nextMerchantId === 'no-merchants-found') {
          onSelectionChange(null, null);
          return;
        }

        const selectedMerchant = merchantsData?.adminListMerchants.find((merchant) => merchant.id === nextMerchantId);
        const selectedMerchantName = selectedMerchant?.name || `Merchant ${nextMerchantId}`;

        onSelectionChange(nextMerchantId, selectedMerchantName);
      }}
      isDisabled={!selectedFamilyId || merchantsLoading}
      items={merchants}
      className={className}
    >
      {(item) => (
        <Select.Item id={item.id} isDisabled={item.isDisabled}>
          {item.label}
        </Select.Item>
      )}
    </Select>
  );
};

export default AdminSelectMerchant;
